/**
 * TableRowWithBackground extends the stock TableRow node with a `backgroundColor`
 * attribute that applies a subtle tinted background to an entire table row.
 *
 * ## Storage strategy — two mechanisms, one extension
 *
 * **TableBlock** stores TipTap JSON directly, so `backgroundColor` is persisted
 * as a node attribute and survives saves/reloads with no extra work.
 *
 * **MarkdownBlock** stores markdown text. TipTap JSON is re-derived on every
 * load via tiptap-markdown, which ignores unknown node attributes when
 * serialising back to markdown. To persist row colors in MarkdownBlock,
 * the agent embeds a `{bg:danger}` marker at the start of the first cell's
 * text (e.g. `| {bg:danger} Kommentar text | ... |`). A ProseMirror decoration
 * plugin picks this up visually (background + hidden marker text) without
 * modifying the document — so the marker is preserved in the markdown
 * on every save and the color is faithfully re-applied on every load.
 *
 * Approach (b) — post-processing TipTap JSON — was rejected because it
 * dispatches transactions that interfere with the MarkdownBlock save cycle.
 * Approach (c) — separate metadata — was rejected because row-index keys
 * are fragile when rows are inserted/deleted.
 */

import { TableRow } from "@tiptap/extension-table-row";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

export type RowBackgroundColor = "danger" | "warning" | "success" | null;

/** Named values → screen rgba colors. Tuned for readability on white. */
export const ROW_BACKGROUND_COLORS: Record<string, string> = {
  danger: "rgba(239, 68, 68, 0.08)",
  warning: "rgba(245, 158, 11, 0.08)",
  success: "rgba(34, 197, 94, 0.08)",
};

/** Matches `{bg:danger}`, `{bg:warning}`, `{bg:success}` at the start of text. */
const BG_MARKER_RE = /^\{bg:(danger|warning|success)\} ?/;

const pluginKey = new PluginKey("tableRowBackground");

export const TableRowWithBackground = TableRow.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      backgroundColor: {
        default: null,
        // Persist in TipTap JSON (TableBlock path)
        parseHTML: (element) => element.getAttribute("data-bg-color") ?? null,
        renderHTML: (attributes) => {
          if (!attributes.backgroundColor) return {};
          return { "data-bg-color": attributes.backgroundColor };
        },
      },
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: pluginKey,
        props: {
          decorations(state) {
            const decorations: Decoration[] = [];

            state.doc.descendants((node, pos) => {
              if (node.type.name !== "tableRow") return;

              // --- Attribute-based (TableBlock: stored in JSON) ---
              const attrColor = node.attrs.backgroundColor as RowBackgroundColor;
              if (attrColor && ROW_BACKGROUND_COLORS[attrColor]) {
                decorations.push(
                  Decoration.node(pos, pos + node.nodeSize, {
                    style: `background-color: ${ROW_BACKGROUND_COLORS[attrColor]}`,
                    "data-row-bg": attrColor,
                  }),
                );
                return;
              }

              // --- Marker-based (MarkdownBlock: {bg:danger} in first cell text) ---
              const firstCell = node.firstChild;
              if (!firstCell) return;
              const firstPara = firstCell.firstChild;
              if (!firstPara || firstPara.type.name !== "paragraph") return;
              const firstText = firstPara.firstChild;
              if (!firstText || firstText.type.name !== "text") return;

              const match = firstText.text?.match(BG_MARKER_RE);
              if (!match) return;

              const markerColor = match[1];
              const cssColor = ROW_BACKGROUND_COLORS[markerColor];
              if (!cssColor) return;

              // Apply row background
              decorations.push(
                Decoration.node(pos, pos + node.nodeSize, {
                  style: `background-color: ${cssColor}`,
                  "data-row-bg": markerColor,
                }),
              );

              // Hide the marker text visually.
              // Position breakdown: pos (tableRow open) + 1 (cell open) + 1 (para open) + 1 (text start)
              const markerFrom = pos + 3;
              const markerTo = markerFrom + match[0].length;
              decorations.push(
                Decoration.inline(markerFrom, markerTo, {
                  style: "font-size: 0; line-height: 0;",
                }),
              );
            });

            return DecorationSet.create(state.doc, decorations);
          },
        },
      }),
    ];
  },
});
